import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { cn } from "cn";
import { format } from "date-fns";
import { type ReactNode, useState } from "react";

import { Calendar } from "@/components/ui/calendar/calendar";
import { BwCalendar4Line } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs/tabs-contained";

const DAY_TAB = "day";
const NONE_TAB = "none";

/** Pure. A calendar day (YYYY-MM-DD) as the calendar holds it: that day, at midnight where the person is. */
export function dateOfDay(day: string): Date {
  const [year = 1970, month = 1, date = 1] = day.split("-").map(Number);
  return new Date(year, month - 1, date);
}

/** Pure. The calendar day of a date the person picked. */
export function dayOfDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/** Pure. A calendar day in words, whatever the time zone: "2026-11-30" → "Nov 30, 2026". */
export function dayWords(day: string): string {
  return format(dateOfDay(day), "MMM d, yyyy");
}

type DayPickerProps = FieldWrapperInjectedProps & {
  /** The day (YYYY-MM-DD), or null for none. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** The earliest day the person may pick; days before it are greyed out. */
  min?: string;
  /** The person may choose none, which this names ("No expiry"); absent: a day is required. */
  noneLabel?: string;
  /** A line under the choice shown only while none is chosen (what none means). */
  noneDescription?: ReactNode;
  disabled?: boolean;
  /**
   * The day the Date tab sets when the person moves from none to a day; the calendar opens on `value ?? startDay`.
   * Null: no day to start from, so the Date tab opens the calendar with no day chosen yet.
   */
  startDay: string | null;
  /** Applied to the button that opens the calendar. */
  className?: string;
};

/**
 * A calendar day, as the office's quote dialogs pick one: a button showing the day that opens the calendar, days
 * before `min` greyed out; where none is allowed, a choice between a day and none ("No expiry").
 */
export function DayPicker({
  id,
  value,
  onChange,
  min,
  noneLabel,
  noneDescription,
  disabled,
  startDay,
  className,
  ...aria
}: DayPickerProps) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const openOn = value ?? startDay;
  const picker = (
    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          disabled={disabled}
          {...aria}
          className={cn(
            /* sorted */
            "flex",
            "items-center gap-2",
            "h-10 w-full",
            "px-3 py-2.5",
            "text-sm text-left",
            "border border-border-dark-low rounded-lg cursor-pointer",
            "disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:border-border-dark-high aria-invalid:border-border-negative",
            className,
          )}
        >
          <BwCalendar4Line size={20} className="text-text-dark-min" />
          <span className={value ? "text-text-dark-max" : "text-text-dark-min"}>
            {value ? dayWords(value) : "Pick a day"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          required
          fixedWeeks
          selected={value === null ? undefined : dateOfDay(value)}
          defaultMonth={openOn === null ? undefined : dateOfDay(openOn)}
          onSelect={(date: Date) => {
            onChange(dayOfDate(date));
            setCalendarOpen(false);
          }}
          disabled={min === undefined ? undefined : { before: dateOfDay(min) }}
        />
      </PopoverContent>
    </Popover>
  );
  if (noneLabel === undefined) return picker;
  // With no day yet, the Date tab is the open calendar: closing it without a
  // pick goes back to none, so the tab always matches the stored value.
  const dayChosen = value !== null || calendarOpen;
  const chooseTab = (tab: string) => {
    if (tab === NONE_TAB) {
      setCalendarOpen(false);
      onChange(null);
      return;
    }
    if (startDay !== null) {
      onChange(startDay);
      return;
    }
    // Opened after the click: the tab takes focus once React has committed,
    // which would dismiss a calendar opened in the same event.
    setTimeout(() => setCalendarOpen(true), 0);
  };
  return (
    <div className="flex flex-col gap-2">
      <Tabs value={dayChosen ? DAY_TAB : NONE_TAB} onValueChange={chooseTab}>
        <TabsList className="w-auto">
          <TabsTrigger value={DAY_TAB} disabled={disabled}>
            Date
          </TabsTrigger>
          <TabsTrigger value={NONE_TAB} disabled={disabled}>
            {noneLabel}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {dayChosen ? (
        picker
      ) : noneDescription ? (
        <p className="text-sm text-text-dark-low">{noneDescription}</p>
      ) : null}
    </div>
  );
}
